import { Button, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { GoSignOut } from "react-icons/go";
import AccountTypes from 'context/accountTypes';
import Utils from 'context/utils';
import SessionCountdown from 'components/controls/sessionCountdown';
import './actingPill.css';

/**
 * ActingPill — the persistent indicator that the console is being driven as somebody else.
 *
 * Fixed bottom-CENTRE, out of flow so page height is unaffected (a hard requirement), z-indexed
 * above page content but below Bootstrap's modal layer (1050) so the session-ended modal covers it
 * rather than the other way round. Bottom-left is taken by ReactFlow's <Controls> on the editor, the
 * playground graph widget and the two Mission Control canvases; bottom-right is where those same
 * canvases place ReactFlow's own attribution link — centre is the one spot clear on every surface.
 *
 * Styled as a compact `alert alert-warning` with a 3px warning-coloured left bar, this codebase's
 * established accent-bar convention (the settings sub-nav's 3px left border, the primary tabs' 3px
 * top border) on the warning palette rather than the orange nav accent.
 *
 * Reads as one sentence, on one line — "Acting as <name> · Expires in mm:ss" — using a middle dot
 * as the separator, this codebase's established punctuation for single-line meta text (the page
 * title, the mission switcher's current-mission note, a thread item's meta row). The name is the
 * only emphasised part and the only part allowed to shrink: it truncates with an ellipsis rather
 * than pushing the rest of the sentence, or the pill itself, past the viewport's edge on a narrow
 * window — the lead text, separator, countdown and exit control all keep their natural size.
 *
 * The live countdown to `session.expiresAt`, and the guarded call into
 * `api.checkAndEndExpiredSession()` when it reaches zero, live in the shared `SessionCountdown`
 * component (`components/controls/sessionCountdown`) — the user menu renders the identical
 * component against the identical session, so there is exactly one timer/cleanup/zero-handling code
 * path rather than two that could drift apart.
 *
 * The exit control's tooltip is react-bootstrap's `Tooltip` + `OverlayTrigger` (this codebase's
 * established idiom, see `components/controls/InfoTooltip`), not the browser's native `title`. The
 * `aria-label` stays regardless — a tooltip is not an accessible name.
 */
export default function ActingPill({ session, api, onExit }) {
  if ( !session ) {
    return null;
  }

  const Glyph = AccountTypes.of(session).glyph;
  const label = session.name || Utils.shortId(session.user_id);

  return (
    <div className="acting-pill alert alert-warning" role="alert">
      <Glyph className="acting-pill-glyph"/>
      <div className="acting-pill-body">
        <span className="acting-pill-lead">Acting as</span>
        <span className="acting-pill-name" title={session.user_id}>{label}</span>
        <span className="acting-pill-sep" aria-hidden="true">&middot;</span>
        <SessionCountdown className="acting-pill-countdown" expiresAt={session.expiresAt} api={api}/>
      </div>
      <OverlayTrigger placement="top" overlay={<Tooltip>Return to your own session</Tooltip>}>
        <Button
          variant="light"
          size="sm"
          className="acting-pill-exit"
          aria-label="Return to your own session"
          onClick={onExit}
        ><GoSignOut/></Button>
      </OverlayTrigger>
    </div>
  );
}
